import React, { useState } from 'react';
import Layout from './Layout';
import OverviewScreen from '../screens/OverviewScreen';
import CrewMovementScreen from '../screens/CrewMovementScreen';
import CounterDistributionScreen from '../screens/CounterDistributionScreen';
import ReportsScreen from '../screens/ReportsScreen';
import AdminScreen from '../screens/AdminScreen';
import SecurityScreen from '../screens/SecurityScreen';
import MyAccountScreen from '../screens/MyAccountScreen';
import { Tab, UserRole } from '../types';
import { useAuth } from '../contexts/AuthContext';
import { ChartPieIcon, UsersIcon, TableCellsIcon, DocumentChartBarIcon, CogIcon, BarcodeIcon, ShieldCheckIcon } from './Icons';
import AppTour from './AppTour';

interface MainLayoutProps {
  initialTab: Tab;
  showTour: boolean;
  onTourComplete: () => void;
}

const MainLayout: React.FC<MainLayoutProps> = ({ initialTab, showTour, onTourComplete }) => {
  const [activeTab, setActiveTab] = useState<Tab>(initialTab);
  const { user } = useAuth();

  const TABS_CONFIG = [
    { 
      id: Tab.Overview, 
      label: 'Overview', 
      icon: <ChartPieIcon className="w-5 h-5 mr-2"/>, 
      roles: [UserRole.ADMIN, UserRole.SUPERVISOR, UserRole.AGENT, UserRole.VIEWER], 
      activeColor: 'text-emerald-400',
      activeBorderColor: 'border-emerald-400',
      activeBgColor: 'bg-emerald-900/20'
    },
    { 
      id: Tab.CrewMovement, 
      label: 'Crew Movement', 
      icon: <UsersIcon className="w-5 h-5 mr-2"/>, 
      roles: [UserRole.ADMIN, UserRole.SUPERVISOR, UserRole.AGENT], 
      activeColor: 'text-sky-400',
      activeBorderColor: 'border-sky-400',
      activeBgColor: 'bg-sky-900/20'
    },
    { 
      id: Tab.CounterDistribution, 
      label: 'Counter Distribution', 
      icon: <TableCellsIcon className="w-5 h-5 mr-2"/>, 
      roles: [UserRole.ADMIN, UserRole.SUPERVISOR], 
      activeColor: 'text-amber-400',
      activeBorderColor: 'border-amber-400',
      activeBgColor: 'bg-amber-900/20'
    },
    { 
      id: Tab.Security, 
      label: 'Security', 
      icon: <BarcodeIcon className="w-5 h-5 mr-2"/>, 
      roles: [UserRole.ADMIN, UserRole.SUPERVISOR, UserRole.AGENT], 
      activeColor: 'text-rose-400',
      activeBorderColor: 'border-rose-400',
      activeBgColor: 'bg-rose-900/20'
    },
    { 
      id: Tab.MyAccount, 
      label: 'My Account', 
      icon: <ShieldCheckIcon className="w-5 h-5 mr-2"/>, 
      roles: [UserRole.ADMIN, UserRole.SUPERVISOR, UserRole.AGENT, UserRole.VIEWER], 
      activeColor: 'text-fuchsia-400',
      activeBorderColor: 'border-fuchsia-400',
      activeBgColor: 'bg-fuchsia-900/20'
    },
    { 
      id: Tab.Reports, 
      label: 'Reports', 
      icon: <DocumentChartBarIcon className="w-5 h-5 mr-2"/>, 
      roles: [UserRole.ADMIN, UserRole.SUPERVISOR, UserRole.AGENT, UserRole.VIEWER], 
      activeColor: 'text-indigo-400',
      activeBorderColor: 'border-indigo-400',
      activeBgColor: 'bg-indigo-900/20'
    },
    { 
      id: Tab.Admin, 
      label: 'Admin Panel', 
      icon: <CogIcon className="w-5 h-5 mr-2"/>, 
      roles: [UserRole.ADMIN], 
      activeColor: 'text-slate-400',
      activeBorderColor: 'border-slate-400',
      activeBgColor: 'bg-slate-700/20'
    },
  ];

  const availableTabs = TABS_CONFIG.filter(tab => user && tab.roles.includes(user.role));

  const renderContent = () => {
    switch (activeTab) {
      case Tab.Overview: return <OverviewScreen />;
      case Tab.CrewMovement: return <CrewMovementScreen />;
      case Tab.CounterDistribution: return <CounterDistributionScreen />;
      case Tab.Security: return <SecurityScreen />;
      case Tab.MyAccount: return <MyAccountScreen />;
      case Tab.Reports: return <ReportsScreen />;
      case Tab.Admin: return <AdminScreen />;
      default: return <OverviewScreen />;
    }
  };

  return (
    <Layout>
      <div className="flex flex-col flex-grow">
        <div className="no-print mb-4 border-b border-slate-700 flex justify-between items-center">
          <nav data-tour-id="main-tabs" className="flex flex-wrap -mb-px" aria-label="Tabs">
            {availableTabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`${
                  activeTab === tab.id
                    ? `${tab.activeBgColor} ${tab.activeBorderColor} ${tab.activeColor} font-semibold`
                    : `border-transparent ${tab.activeColor} opacity-70 hover:opacity-100 hover:border-slate-600`
                } flex items-center whitespace-nowrap py-3 px-3 sm:px-4 border-b-2 font-medium text-sm transition-colors duration-200 rounded-t-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 focus:ring-offset-slate-900`}
              >
                {tab.icon}
                {tab.label}
              </button>
            ))}
          </nav>
        </div>
        <div className="flex-grow animate-slide-up-fade-in">
          {renderContent()}
        </div>
      </div>
      <AppTour isOpen={showTour} onComplete={onTourComplete} setActiveTab={setActiveTab} />
    </Layout>
  );
};

export default MainLayout;